import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  syncVirtualNumberPrices,
  fetchVirtualNumberCountries,
  fetchVirtualNumberPrices,
  fetchVirtualNumberProviders,
  toggleVirtualNumberProvider,
  updateVirtualNumberPrice,
  bulkUpdateVirtualNumberPrices,
  setSelectedCountry,
  setSelectedProvider,
  setPage,
  setCountrySearch,
  setServiceSearch,
} from "../../store/slices/virtualNumberSlice";
import { GlassCard } from "../../components/ui/GlassCard";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { CountryFlag } from "../../components/ui/CountryFlag";
import { getServiceIcon } from "../../utils/serviceIcons.js";
import { sortVirtualNumberCountries } from "../../utils/countryOrdering.js";
import toast from "react-hot-toast";
import { FiRefreshCw, FiEdit2, FiCheck, FiX, FiSearch } from "react-icons/fi";

export const AdminVirtualNumbersPage = () => {
  const dispatch = useDispatch();
  const {
    virtualNumberCountries,
    virtualNumberPrices,
    virtualNumberProviders,
    selectedCountry,
    selectedProvider,
    pagination,
    search,
    isLoading,
    syncLoading,
    bulkLoading,
    providersLoading,
  } = useSelector((state) => state.virtualNumbers);

  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [serviceSearchInput, setServiceSearchInput] = useState(
    search.serviceSearch || "",
  );
  const [selectedServiceCodes, setSelectedServiceCodes] = useState([]);
  const [bulkMargin, setBulkMargin] = useState("20");
  const [applyToAllServices, setApplyToAllServices] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);

  // Handle service search with automatic space trimming
  const handleServiceSearchChange = (value) => {
    const trimmed = value.trim();
    setServiceSearchInput(trimmed);
  };

  // Local country search input to allow typing spaces normally.
  // We only dispatch the trimmed search value after a short debounce.
  const [countrySearchInput, setCountrySearchInput] = useState(
    search.countrySearch || "",
  );

  useEffect(() => {
    setCountrySearchInput(search.countrySearch || "");
  }, [search.countrySearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(setCountrySearch(countrySearchInput.trim()));
    }, 300);
    return () => clearTimeout(handler);
  }, [countrySearchInput, dispatch]);

  // Naira conversion rate: ₦1,500 per $1
  const NAIRA_RATE = 1500;

  const formatNaira = (usdAmount) => {
    return `₦${(usdAmount * NAIRA_RATE).toLocaleString("en-NG")}`;
  };

  // Filtered countries based on search and priority ordering
  const filteredCountries = useMemo(() => {
    const filtered = search.countrySearch
      ? virtualNumberCountries.filter(
          (country) =>
            country.countryName
              .toLowerCase()
              .includes(search.countrySearch.toLowerCase()) ||
            country.countryCode
              ?.toLowerCase()
              .includes(search.countrySearch.toLowerCase()),
        )
      : virtualNumberCountries;

    return sortVirtualNumberCountries(filtered);
  }, [virtualNumberCountries, search.countrySearch]);

  const selectedCountryObj = useMemo(
    () =>
      virtualNumberCountries.find((country) => country._id === selectedCountry),
    [virtualNumberCountries, selectedCountry],
  );

  const filteredServices = virtualNumberPrices;

  useEffect(() => {
    dispatch(fetchVirtualNumberProviders());
  }, [dispatch]);

  useEffect(() => {
    if (selectedProvider) {
      dispatch(fetchVirtualNumberCountries({ provider: selectedProvider }));
    }
  }, [selectedProvider, dispatch]);

  useEffect(() => {
    setServiceSearchInput(search.serviceSearch || "");
  }, [search.serviceSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(setServiceSearch(serviceSearchInput));
      dispatch(setPage(1));
    }, 300);

    return () => clearTimeout(handler);
  }, [serviceSearchInput, dispatch]);

  useEffect(() => {
    if (selectedCountry && selectedProvider) {
      dispatch(
        fetchVirtualNumberPrices({
          countryId: selectedCountry,
          provider: selectedProvider,
          page: pagination.page,
          limit: pagination.limit,
          serviceSearch: search.serviceSearch,
        }),
      );
    }
  }, [
    selectedCountry,
    selectedProvider,
    pagination.page,
    search.serviceSearch,
    dispatch,
  ]);

  const handleSync = async () => {
    setShowSyncConfirm(true);
  };

  const handleConfirmSync = async () => {
    setShowSyncConfirm(false); // Close modal immediately
    dispatch(syncVirtualNumberPrices(selectedProvider || "herosms")); // Let sync run in background
  };

  const handleEditPrice = (id, currentPrice) => {
    setEditingId(id);
    setEditPrice(currentPrice.toString());
  };

  const handleSavePrice = async (id) => {
    if (!editPrice || isNaN(editPrice) || parseFloat(editPrice) < 0) {
      toast.error("Invalid price");
      return;
    }
    try {
      await dispatch(
        updateVirtualNumberPrice({
          id,
          data: { adminSellingPrice: parseFloat(editPrice) },
        }),
      );
      setEditingId(null);
      setEditPrice("");
    } catch (error) {
      console.error("Price update failed:", error);
    }
  };

  const handleToggleEnable = async (id, isEnabled) => {
    try {
      await dispatch(
        updateVirtualNumberPrice({
          id,
          data: { isEnabled: !isEnabled },
        }),
      );
    } catch (error) {
      console.error("Toggle enable failed:", error);
    }
  };

  const toggleServiceSelection = (serviceCode) => {
    setApplyToAllServices(false);
    setSelectedServiceCodes((prev) =>
      prev.includes(serviceCode)
        ? prev.filter((code) => code !== serviceCode)
        : [...prev, serviceCode],
    );
  };

  const handleSelectAllVisible = () => {
    setApplyToAllServices(false);
    const visibleCodes = filteredServices.map((price) => price.serviceCode);
    const allSelected =
      visibleCodes.length > 0 &&
      visibleCodes.every((code) => selectedServiceCodes.includes(code));
    setSelectedServiceCodes(allSelected ? [] : visibleCodes);
  };

  const handleToggleApplyToAllServices = () => {
    setSelectedServiceCodes([]);
    setApplyToAllServices((prev) => !prev);
  };

  const handleApplyMargin = async () => {
    const margin = Number(bulkMargin);
    if (isNaN(margin) || margin < 0) {
      toast.error("Enter a valid margin percentage");
      return;
    }

    if (!applyToAllServices && selectedServiceCodes.length === 0) {
      toast.error("Select services or choose all services");
      return;
    }

    try {
      const payload = {
        countryId: selectedCountry,
        provider: selectedProvider,
        margin,
        applyToAll: applyToAllServices,
      };

      if (!applyToAllServices) {
        payload.updates = virtualNumberPrices
          .filter((price) => selectedServiceCodes.includes(price.serviceCode))
          .map((price) => ({
            serviceCode: price.serviceCode,
            adminSellingPrice: Number(
              (Number(price.providerCost || 0) * (1 + margin / 100)).toFixed(2),
            ),
          }));
      }

      await dispatch(bulkUpdateVirtualNumberPrices(payload)).unwrap();
      setSelectedServiceCodes([]);
      setApplyToAllServices(false);
      dispatch(
        fetchVirtualNumberPrices({
          countryId: selectedCountry,
          provider: selectedProvider,
          page: pagination.page,
          limit: pagination.limit,
          serviceSearch: search.serviceSearch,
        }),
      );
    } catch (error) {
      console.error("Bulk update failed:", error);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Virtual Numbers Management
          </h1>
          <p className="text-gray-600">
            Manage pricing and sync services from multiple providers
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncLoading}
          className="flex items-center space-x-2 bg-linear-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
        >
          <FiRefreshCw
            className={`${syncLoading ? "animate-spin" : ""}`}
            size={18}
          />
          <span>
            {syncLoading
              ? "Syncing..."
              : `Sync from ${virtualNumberProviders.find((p) => p.providerId === selectedProvider)?.displayName || "Provider"}`}
          </span>
        </button>
      </div>

      {/* Provider Tabs */}
      {virtualNumberProviders.length > 0 && (
        <GlassCard className="p-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {virtualNumberProviders.map((provider) => (
              <div
                key={provider.providerId}
                className="flex items-center gap-2"
              >
                <button
                  onClick={() =>
                    dispatch(setSelectedProvider(provider.providerId))
                  }
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedProvider === provider.providerId
                      ? "bg-primary-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {provider.displayName}
                </button>
                <button
                  onClick={() =>
                    dispatch(
                      toggleVirtualNumberProvider({
                        providerId: provider.providerId,
                        isEnabled: !provider.isEnabled,
                      }),
                    )
                  }
                  className={`px-3 py-2 rounded text-sm font-medium transition-all ${
                    provider.isEnabled
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-red-100 text-red-700 hover:bg-red-200"
                  }`}
                  title={
                    provider.isEnabled ? "Click to disable" : "Click to enable"
                  }
                >
                  {provider.isEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Country selector */}
      {virtualNumberCountries.length > 0 && (
        <GlassCard className="p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Country Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Search Countries
              </label>
              <div className="relative">
                <FiSearch
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearchInput}
                  onChange={(e) => setCountrySearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div className="mt-4 max-h-64 overflow-y-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country._id}
                      type="button"
                      onClick={() => dispatch(setSelectedCountry(country._id))}
                      className="w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <CountryFlag
                          countryCode={country?.countryCode}
                          countryName={country?.countryName}
                          className="h-7 w-7 rounded-md object-cover"
                        />
                        <div className="font-bold text-gray-900">
                          {country.countryName}
                        </div>
                      </div>
                      {/* <div className="text-sm text-gray-500">
                        {country.countryCode.toUpperCase()} ·{" "}
                        {country.enabledCount}/{country.serviceCount} services
                      </div> */}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No countries match your search.
                  </div>
                )}
              </div>
            </div>

            {/* Country Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Country
              </label>
              <div className="flex items-center gap-3 mb-3">
                <CountryFlag
                  countryCode={selectedCountryObj?.countryCode}
                  countryName={selectedCountryObj?.countryName}
                  className="h-7 w-11 rounded-md object-cover"
                />
                <span className="text-sm text-gray-600">
                  {selectedCountryObj
                    ? selectedCountryObj.countryName
                    : "Choose a country"}
                </span>
              </div>
              <select
                value={selectedCountry || ""}
                onChange={(e) =>
                  dispatch(setSelectedCountry(Number(e.target.value)))
                }
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
              >
                <option value="">Select a country...</option>
                {filteredCountries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.countryName} ({country.enabledCount}/
                    {country.serviceCount} services)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Services section - always show search when country selected */}
      {selectedCountry && (
        <>
          {/* Service Search - always visible when country is selected */}
          <GlassCard className="p-4">
            <div className="relative max-w-md">
              <FiSearch
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search services (spaces auto-trimmed)..."
                value={serviceSearchInput}
                onChange={(e) => handleServiceSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
              />
            </div>
          </GlassCard>

          {isLoading ? (
            <GlassCard className="p-8 text-center">
              <div className="flex justify-center">
                <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 mt-4">Loading services...</p>
            </GlassCard>
          ) : virtualNumberPrices.length === 0 ? (
            <GlassCard className="p-8 text-center">
              <p className="text-gray-600 text-lg">
                No services found for this country
              </p>
            </GlassCard>
          ) : (
            <div className="space-y-4">
              <GlassCard className="p-4 space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-end">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Margin (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={bulkMargin}
                        onChange={(e) => setBulkMargin(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={applyToAllServices}
                          onChange={handleToggleApplyToAllServices}
                          className="h-4 w-4 rounded text-primary-600 border-gray-300"
                        />
                        Apply to all services for{" "}
                        {selectedCountryObj?.countryName || "selected country"}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {applyToAllServices
                          ? "This will update every service for the country using provider cost."
                          : "Select services from the table below and apply the margin to only those services."}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-3 sm:items-end">
                    <button
                      onClick={handleApplyMargin}
                      disabled={
                        (!applyToAllServices &&
                          selectedServiceCodes.length === 0) ||
                        !selectedCountry ||
                        bulkLoading
                      }
                      className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bulkLoading ? "Applying..." : "Apply margin"}
                    </button>
                    <div className="text-sm text-gray-600">
                      {applyToAllServices
                        ? `All services will be updated for ${selectedCountryObj?.countryName || "this country"}.`
                        : `${selectedServiceCodes.length} service${selectedServiceCodes.length === 1 ? "" : "s"} selected.`}
                    </div>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-linear-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            checked={
                              filteredServices.length > 0 &&
                              filteredServices.every((price) =>
                                selectedServiceCodes.includes(
                                  price.serviceCode,
                                ),
                              )
                            }
                            onChange={handleSelectAllVisible}
                            className="h-4 w-4 rounded text-primary-600 border-gray-300"
                          />
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                          Service
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                          Code
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                          Provider Cost
                          <br />
                          <span className="text-xs text-gray-500">
                            USD / NGN
                          </span>
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                          Admin Price
                          <br />
                          <span className="text-xs text-gray-500">
                            USD / NGN
                          </span>
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                          Markup
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                          Available
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                          Status
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredServices.map((price, index) => (
                        <tr
                          key={price._id}
                          className={`${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                          } hover:bg-primary-50 transition-colors`}
                        >
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedServiceCodes.includes(
                                price.serviceCode,
                              )}
                              onChange={() =>
                                toggleServiceSelection(price.serviceCode)
                              }
                              className="h-4 w-4 rounded text-primary-600 border-gray-300"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <span className="text-xl text-primary-600">
                                {React.createElement(
                                  getServiceIcon(
                                    price.serviceName,
                                    price.serviceCode,
                                  ),
                                )}
                              </span>
                              <span className="font-medium text-gray-900">
                                {price.serviceName}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600 font-mono bg-gray-100 px-3 py-1 rounded">
                              {price.serviceCode}
                            </span>
                          </td>
                          {(() => {
                            const providerCost = Number(
                              price.providerCost ?? 0,
                            );
                            const adminSellingPrice = Number(
                              price.adminSellingPrice ?? 0,
                            );
                            const markupText =
                              providerCost > 0 && adminSellingPrice > 0
                                ? `+${((adminSellingPrice / providerCost - 1) * 100).toFixed(0)}%`
                                : "N/A";
                            return (
                              <>
                                <td className="px-6 py-4 text-right">
                                  <div className="text-sm">
                                    <div className="text-gray-700">
                                      ${providerCost.toFixed(2)}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      {formatNaira(providerCost)}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  {editingId === price._id ? (
                                    <input
                                      type="number"
                                      value={editPrice}
                                      onChange={(e) =>
                                        setEditPrice(e.target.value)
                                      }
                                      className="w-24 px-3 py-2 rounded-lg border border-primary-300 text-right focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                                      step="0.01"
                                      min="0"
                                      autoFocus
                                    />
                                  ) : (
                                    <div className="text-sm">
                                      <div className="font-semibold text-primary-600">
                                        ${adminSellingPrice.toFixed(2)}
                                      </div>
                                      <div className="text-gray-500 text-xs">
                                        {formatNaira(adminSellingPrice)}
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <span
                                    className={`text-sm font-medium ${
                                      providerCost > 0
                                        ? "text-green-600"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {markupText}
                                  </span>
                                </td>
                              </>
                            );
                          })()}
                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                price.availableCount > 0
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {price.availableCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() =>
                                handleToggleEnable(price._id, price.isEnabled)
                              }
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                price.isEnabled
                                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {price.isEnabled ? "✓ Enabled" : "✗ Disabled"}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex gap-2 justify-center">
                              {editingId === price._id ? (
                                <>
                                  <button
                                    onClick={() => handleSavePrice(price._id)}
                                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Save"
                                  >
                                    <FiCheck size={18} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingId(null);
                                      setEditPrice("");
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                    title="Cancel"
                                  >
                                    <FiX size={18} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleEditPrice(
                                      price._id,
                                      price.adminSellingPrice,
                                    )
                                  }
                                  className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                                  title="Edit price"
                                >
                                  <FiEdit2 size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center items-center space-x-4 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() =>
                        dispatch(setPage(Math.max(1, pagination.page - 1)))
                      }
                      disabled={pagination.page === 1}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <span className="text-gray-600 font-medium">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() =>
                        dispatch(
                          setPage(
                            Math.min(pagination.pages, pagination.page + 1),
                          ),
                        )
                      }
                      disabled={pagination.page === pagination.pages}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </GlassCard>
            </div>
          )}
        </>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showSyncConfirm}
        title="Sync Virtual Numbers?"
        message={`This will fetch all countries and services from ${virtualNumberProviders.find((p) => p.providerId === selectedProvider)?.displayName || "the selected provider"} and update the database. Admin edits will be preserved. This may take a few minutes.`}
        confirmText="Sync Now"
        cancelText="Cancel"
        onConfirm={handleConfirmSync}
        onCancel={() => setShowSyncConfirm(false)}
        isLoading={syncLoading}
      />
    </div>
  );
};
